import {
  createPublicClient,
  defineChain,
  fallback,
  http,
  type Address,
  type PublicClient,
} from 'viem';

import { getActiveChainConfig } from './config';

const clients = new Map<number, PublicClient>();
const MAX_RPC_ATTEMPTS = 4;
const BASE_BACKOFF_MS = 250;

function rotateRpcUrls(urls: string[], seed: number): string[] {
  if (urls.length <= 1) return urls;
  const offset = Math.abs(seed) % urls.length;
  return [...urls.slice(offset), ...urls.slice(0, offset)];
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function withRpcBackoff<T>(work: () => Promise<T>): Promise<T> {
  let delayMs = BASE_BACKOFF_MS;
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_RPC_ATTEMPTS; attempt += 1) {
    try {
      return await work();
    } catch (error) {
      lastError = error;
      if (attempt === MAX_RPC_ATTEMPTS - 1) break;
      const jitter = Math.floor(Math.random() * 150);
      await delay(delayMs + jitter);
      delayMs = Math.min(delayMs * 2, 2_000);
    }
  }

  throw lastError;
}

function getActiveClient(): PublicClient {
  const chainConfig = getActiveChainConfig();
  const cached = clients.get(chainConfig.id);
  if (cached) return cached;
  const rotatedRpcUrls = rotateRpcUrls(chainConfig.rpcUrls, Date.now() + chainConfig.id);

  const chain = defineChain({
    id: chainConfig.id,
    name: chainConfig.chainName,
    nativeCurrency: chainConfig.nativeCurrency,
    rpcUrls: {
      default: { http: rotatedRpcUrls },
      public: { http: rotatedRpcUrls },
    },
    blockExplorers: {
      default: { name: 'PulseScan', url: chainConfig.blockExplorerUrls[0] },
    },
    contracts: {
      multicall3: {
        address: chainConfig.multicall3 as Address,
        blockCreated: Number(chainConfig.deploymentStartBlock),
      },
    },
  });

  const client = createPublicClient({
    chain,
    transport: fallback(
      rotatedRpcUrls.map((url) => http(url, { timeout: 8000, retryCount: 2, retryDelay: 200 })),
      { rank: true },
    ),
    pollingInterval: 10000,
    batch: {
      multicall: {
        batchSize: 1024 * 150,
        wait: 16,
      },
    },
  });

  clients.set(chainConfig.id, client);
  return client;
}

export const readClient = {
  readContract: (...args: Parameters<PublicClient['readContract']>) => withRpcBackoff(() => getActiveClient().readContract(...args)),
  getLogs: (...args: Parameters<PublicClient['getLogs']>) => withRpcBackoff(() => getActiveClient().getLogs(...args)),
  getTransactionReceipt: (...args: Parameters<PublicClient['getTransactionReceipt']>) => withRpcBackoff(() => getActiveClient().getTransactionReceipt(...args)),
  getBlockNumber: (...args: Parameters<PublicClient['getBlockNumber']>) => withRpcBackoff(() => getActiveClient().getBlockNumber(...args)),
  getBlock: (...args: Parameters<PublicClient['getBlock']>) => withRpcBackoff(() => getActiveClient().getBlock(...args)),
  getBalance: (...args: Parameters<PublicClient['getBalance']>) => withRpcBackoff(() => getActiveClient().getBalance(...args)),
  multicall: (...args: Parameters<PublicClient['multicall']>) => withRpcBackoff(() => getActiveClient().multicall(...args)),
};

export async function readContractsBatch<T>(
  contracts: Array<{
    address: Address;
    abi: any;
    functionName: string;
    args?: readonly unknown[];
  }>
): Promise<T[]> {
  try {
    const chainConfig = getActiveChainConfig();
    const results = await readClient.multicall({
      contracts: contracts as any,
      allowFailure: true,
      multicallAddress: chainConfig.multicall3 as Address,
    });

    return results.map((r) => ((r as any).status === 'success' ? ((r as any).result as T) : (null as T)));
  } catch {
    const out: T[] = [];
    for (const c of contracts) {
      try {
        const result = (await readClient.readContract({
          address: c.address,
          abi: c.abi,
          functionName: c.functionName as any,
          args: c.args,
        })) as T;
        out.push(result);
      } catch {
        out.push(null as T);
      }
    }
    return out;
  }
}
